/**
 * @module experience/shop/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/*tslint:disable:max-file-line-count */
// Interfaces
import {
  Product,
  ColorOption,
  MemoryOption,
  Features,
  ItemDetail,
  Specification,
  Promotion,
  PricingOptions,
  SkuPriceDetails,
  DeviceSimInfo,
} from '@support/api/common/product.interface';
import {
  ProductDetails,
  MemoryMap,
} from '@experience/shop/product-details/interfaces/product-details.interface';
// Services
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';

export class ProductDetailsDefaultModel {
  /**
   *  Variable to product details
   */
  public productDetails: ProductDetails;
  /**
   *  Variable to store offer id
   */
  public offerId: string;
  /**
   *  Variable to store family name
   */
  private familyName: string;
  /**
   *  Variable to store sub type
   */
  private subType: string;
  /**
   *  Variable to store type
   */
  private type: string;
  /**
   *  Variable to store product type
   */
  private isFeatured: string;
  /**
   *  Variable to store color options array
   */
  private colorOptions: ColorOption[];
  /**
   *  Variable to store memory options array
   */
  private memoryOptions: MemoryOption[];
  /**
   *  Variable to store features array
   */
  private features: Features[];
  /**
   *  Variable to store whether the device has deal or not
   */
  private hasDeal: boolean;
  /**
   *  Variable to store product sub type
   */
  public productSubType: string;
  /**
   *  Variable to specifications
   */
  private specifications: Specification[];
  /**
   *  Variable to store item details
   */
  private itemDetails: ItemDetail[];
  /**
   *  Variable to store sku details
   */
  private sku: ItemDetail[];
  /**
   *  Variable to store product type
   */
  private productType: string;
  /**
   * Stores whether cpo link is clicked
   */
  private isCpoClicked: boolean;
  /**
   *  Variable to store selected color
   */
  public selectedColor: string;

  private objServiceRef: ObjectService;
  private familyCode: string;
  public hasCPO: boolean;
  /**
   * variable to hold hasNew items flag
   */
  public hasNew: boolean;
  public selectedMemory: string;

  /**
   * Stores current DeviceSimInfo selection
   */
  public selectedSimInfo: DeviceSimInfo;
  /**
   *  Variable to store reviewsCount
   */
  private reviewsCount: number;
  /**
   *  sku offering Id
   */
  public skuId: string;

  /**
   * Long description of the product, to be used in features section
   */
  public longDescription: string;

  /**
   * Description of the product
   */
  public description: string;
  /**
   * Legal description of the product, to be used in features section
   */
  public legalText: string;
  /**
   * Stores the sorted pricing options for Epin
   */
  public sortedPricingOptions: PricingOptions[];

  /**
   * Condition of the device
   */
  public condition: string;
  /**
   * stores pricing options
   */
  public pricingOptions: PricingOptions[];
  /**
   * Average overall rating for the device
   */
  public averageOverallRating: number;
  public memoryMap: MemoryMap[];

  constructor(
    productData: Product,
    objectService: ObjectService,
    color: string,
    commonUtilService: CommonUtilService,
    itemDetails?: ItemDetail[]
  ) {
    this.isCpoClicked = false;
    if (productData) {
      this.familyCode = productData.familyCode;
      this.objServiceRef = objectService;
      this.selectedColor = color;
      this.hasCPO = objectService.get(productData, 'hasCPO', false);
      this.hasNew = objectService.get(productData, 'hasNew', false);
      this.offerId = objectService.get(productData, 'familyId');
      this.familyName = objectService.get(productData, 'familyName');
      this.subType = objectService.get(productData, 'subType');
      this.type = objectService.get(productData, 'type');
      this.isFeatured = objectService.get(productData, 'isFeatured');
      this.colorOptions = objectService.get(productData, 'colorOptions', []);
      this.memoryOptions = objectService.get(productData, 'memoryOptions', []);
      this.features = objectService.get(productData, 'features');
      this.hasDeal = objectService.get(productData, 'hasDeal');
      this.legalText = objectService.get(productData, 'legalText');
      this.productSubType = objectService.get(productData, 'productSubType');
      this.specifications = objectService.get(productData, 'specifications', []);
      this.sku = objectService.get(productData, 'itemDetails');
      this.itemDetails = itemDetails || objectService.get(productData, 'itemDetails');
      this.productType = objectService.get(productData, 'productType', '');
      this.averageOverallRating = objectService.get(productData, 'averageStarRating', 0);
      this.reviewsCount = objectService.get(productData, 'reviewsCount', 0);
      this.longDescription = objectService.get(productData, 'longDescription');
      this.description = objectService.get(productData, 'description');
      this.condition = objectService.get(productData, 'condition');
      this.pricingOptions = objectService.get(productData, 'pricingOptions', []);
      this.memoryMap = this.getMemoryMap();
    }
  }

  /**
   * Returns the subtype of the product
   * @returns string
   */
  public getProductSubType(): string {
    return this.productSubType || '';
  }

  /**
   * Method to get item detail
   * @return ItemDetail[]
   */
  public getItemDetails(): ItemDetail[] {
    return this.itemDetails;
  }

  /**
   * Method to check whether the product is Epin or not
   * @returns boolean
   */
  public isEpin(): boolean {
    return (
      this.productSubType &&
      this.productSubType.toUpperCase() === HARDGOODS_CONSTANTS.VOUCHER &&
      this.productType &&
      this.productType.toLowerCase() === HARDGOODS_CONSTANTS.FINANCIAL
    );
  }
  /**
   * Method to set the CPO clicked value
   * @returns void
   */
  public setCpoClicked(): void {
    this.isCpoClicked = !this.isCpoClicked;
  }
  /**
   * Method to get the value of cpo Clicked
   */
  public getCpoClicked(): boolean {
    return this.isCpoClicked;
  }

  /**
   * Method to get memory option
   * @return MemoryOption[]
   */
  public getMemoryOptions(): MemoryOption[] {
    const memoryOpt =
      this.memoryOptions && this.memoryOptions.length
        ? this.memoryOptions.map((memory: MemoryOption) => {
            return {
              name: this.getMemoryValue(memory.value),
              value: memory.value,
              available: memory.available,
            };
          })
        : null;
    return memoryOpt;
  }

  /**
   * Method to get MemoryValue
   * @return string
   */
  private getMemoryValue(memoryValue: string): string {
    const memoryType = memoryValue.split(' ');
    return memoryType[0];
  }
  /**
   * Method to get color options
   * @return ColorOption[]
   */
  public getColorOptions(): ColorOption[] {
    return this.colorOptions;
  }

  /**
   * Method to get color options
   * @return ColorOption[]
   */
  public getPricingOptions(commonUtilService: CommonUtilService): PricingOptions[] {
    this.sortedPricingOptions = commonUtilService.sortUtil(
      this.pricingOptions,
      'skuPrice',
      true,
      [],
      true
    );
    return this.sortedPricingOptions;
  }
  /**
   * Method to get all images for all colors
   * @return string[]
   */

  public getCacheImages(): string[] {
    return this.itemDetails
      .filter((itemDetail: ItemDetail) => itemDetail.mainImageUrl)
      .map((itemDetail: ItemDetail) => itemDetail.mainImageUrl);
  }

  /**
   * Method to set selected color varient
   * @param color: string
   * @return ItemDetail
   */
  public setSelectedColorVarient(objService: ObjectService, color?: string): ItemDetail {
    if (!this.isEpin()) {
      let selectedItemDetails;
      this.selectedColor = color ? color : this.selectedColor;
      this.selectedMemory = this.selectedMemory
        ? this.validateMemoryAvailability(objService)
        : this.getAvailableMemory(objService);
      const selectedColorItem = this.itemDetails.find((item: ItemDetail) => {
        if (!this.selectedMemory) {
          selectedItemDetails = item.color === this.selectedColor;
        } else {
          selectedItemDetails =
            item.color === this.selectedColor &&
            this.selectedMemory &&
            objService.get(item, 'memory', '').split(' ')[0] === this.selectedMemory.split(' ')[0];
        }
        return selectedItemDetails;
      });
      const filteredItem = selectedColorItem || this.itemDetails[0];
      this.memoryOptions = this.selectedMemory ? this.setMemoryForColor() : null;
      return this.getSelectedVarient(filteredItem, objService);
    } else {
      return this.getSelectedVarient(this.itemDetails[0], objService);
    }
  }

  /**
   * Method to Get Available Memory
   * @return string
   */
  private getAvailableMemory(objService: ObjectService): string {
    if (this.itemDetails) {
      const selectedItem = this.itemDetails.find((item: ItemDetail) => {
        return item.color === this.selectedColor;
      });
      const selectedColorFirstMemory = objService.get(selectedItem, 'memory');
      return selectedColorFirstMemory;
    }
    return;
  }

  /**
   * Method to check Available Memory
   * @return string
   */
  private validateMemoryAvailability(objService: ObjectService): string {
    const selectedMemoryOption = this.memoryMap.filter((obj: MemoryMap) => {
      return obj.color === this.selectedColor;
    });
    const availableMemoryOption = selectedMemoryOption[0].memoryArr.find((obj: string) => {
      return obj.split(' ')[0].toUpperCase() === this.selectedMemory;
    });
    return availableMemoryOption
      ? this.selectedMemory
      : objService.get(selectedMemoryOption, '0.memoryArr.0').split(' ')[0].toUpperCase();
  }

  /**
   * Function to get the skuPrice with Full Price and With Service from the array of the skuPrices
   * @param {SkuPriceDetails[]} skuPrices
   * @returns {SkuPriceDetails}
   */
  public static getWithServiceFullPrice(skuPrices: SkuPriceDetails[]): SkuPriceDetails {
    return (skuPrices || []).find((skuPriceDetails: SkuPriceDetails) => {
      return (
        skuPriceDetails.priceListLineId === 'WithService' &&
        skuPriceDetails.pricingOptionName === 'FullPrice'
      );
    });
  }

  /**
   * Function to get the skuPrice with Full Price and With Service from the array of the skuPrices
   * @param {SkuPriceDetails[]} skuPrices
   * @returns {SkuPriceDetails}
   */
  public static getWithOutServiceFullPrice(skuPrices: SkuPriceDetails[]): SkuPriceDetails {
    return (skuPrices || []).find((skuPriceDetails: SkuPriceDetails) => {
      return (
        skuPriceDetails.priceListLineId === 'WithoutService' &&
        skuPriceDetails.pricingOptionName === 'FullPrice'
      );
    });
  }

  /**
   * Method to get list or sale price
   * @param filteredItem: ItemDetail
   * @return number
   */
  private getServiceThreshold(filteredItem: ItemDetail, objService: ObjectService): number {
    const serviceThreshold =
      objService.get(filteredItem.skuPrice, '0.serviceThreshold.amount') || 0;
    return serviceThreshold;
  }

  /**
   * Method to get priceWithout Service price
   * @param filteredItem: ItemDetail
   * @return number
   */
  private getPriceWithoutService(filteredItem: ItemDetail, objService: ObjectService): number {
    let priceWithOutService;
    if (objService.get(filteredItem, 'skuPrice')) {
      priceWithOutService = filteredItem.skuPrice.find((value: SkuPriceDetails) => {
        return (
          value.priceListLineId === HARDGOODS_CONSTANTS.WITHOUT_SERVICE &&
          value.pricingOptionName === HARDGOODS_CONSTANTS.FULLPRICE
        );
      });
    }
    return objService.get(priceWithOutService, 'priceSummary.payNowPrice.salePrice.amount') || 0;
  }

  /**
   * Method to get priceWithout Service price
   * @param filteredItem: ItemDetail
   * @return number
   */
  private getPriceWithService(filteredItem: ItemDetail, objService: ObjectService): number {
    let priceWithService;
    if (objService.get(filteredItem, 'skuPrice')) {
      priceWithService = filteredItem.skuPrice.find((value: SkuPriceDetails) => {
        return (
          value.priceListLineId === HARDGOODS_CONSTANTS.WITH_SERVICE &&
          value.pricingOptionName === HARDGOODS_CONSTANTS.FULLPRICE
        );
      });
    }
    return objService.get(priceWithService, 'priceSummary.payNowPrice.salePrice.amount') || 0;
  }

  /**
   * Method to get list or sale price
   * @param filteredItem: ItemDetail
   * @return number
   */
  public setSelectedMemoryVarient(memory: string, objService: ObjectService): ItemDetail {
    const selectedMemory = this.itemDetails.find((item: ItemDetail) => {
      return item.memory === memory && item.color === this.selectedColor;
    });
    this.selectedMemory = selectedMemory.memory.split(' ')[0];
    const filteredIem = selectedMemory || this.itemDetails[0];
    return this.getSelectedVarient(filteredIem, objService);
  }

  /**
   * Set the selected DeviceSimInfo for current device
   * @param simInfo passes selected DeviceSimInfo for device
   */
  public setSelectedSimType(simInfo: DeviceSimInfo): void {
    this.selectedSimInfo = simInfo;
  }

  /**
   * Method to get list price
   * @param filteredItem: ItemDetail
   * @return number
   */
  private getListPrice(filteredItem: ItemDetail, objService: ObjectService): number {
    let priceWithService: SkuPriceDetails = {};
    if (
      this.productType === SHARED_BROWSE_CONSTANTS.ACCESSORY ||
      this.productType.toUpperCase() === SHARED_BROWSE_CONSTANTS.FINANCIAL
    ) {
      priceWithService =
        ProductDetailsDefaultModel.getWithOutServiceFullPrice(filteredItem.skuPrice) || {};
    } else {
      priceWithService =
        ProductDetailsDefaultModel.getWithServiceFullPrice(filteredItem.skuPrice) || {};
    }
    const price =
      objService.get(priceWithService, 'priceSummary.payNowPrice.listPrice.amount') || 0;
    return price;
  }

  /**
   * Method to get sale price
   * @param filteredItem: ItemDetail
   * @return number
   */
  private getSalePrice(filteredItem: ItemDetail, objService: ObjectService): number {
    let priceWithService: SkuPriceDetails = {};
    if (
      this.productType === SHARED_BROWSE_CONSTANTS.ACCESSORY ||
      this.productType.toUpperCase() === SHARED_BROWSE_CONSTANTS.FINANCIAL
    ) {
      priceWithService =
        ProductDetailsDefaultModel.getWithOutServiceFullPrice(filteredItem.skuPrice) || {};
    } else {
      priceWithService =
        ProductDetailsDefaultModel.getWithServiceFullPrice(filteredItem.skuPrice) || {};
    }
    const price =
      objService.get(priceWithService, 'priceSummary.payNowPrice.salePrice.amount') || 0;
    return price;
  }

  /**
   * Method to get promotions
   * @param filteredItem: ItemDetail
   * @return Promotion[]
   */
  public getPromotions(filteredItem: ItemDetail, objService: ObjectService): Promotion[] {
    const promotion = objService.get(
      ProductDetailsDefaultModel.getWithServiceFullPrice(filteredItem.skuPrice),
      'promotions'
    );
    return promotion;
  }

  /**
   * Method to get product details
   * @param null
   * @return ProductDetails
   */
  public getProductDetails(): ProductDetails {
    return {
      hasDeal: this.hasDeal,
      familyCode: '',
      familyName: this.familyName,
      offerId: this.offerId,
      type: this.productType,
      reviewsCount: this.reviewsCount,
      longDescription: this.longDescription,
      legalText: this.legalText,
      description: this.description,
      condition: this.condition,
      color: this.selectedColor,
      rating: this.reviewsCount,
      averageOverallRating: this.averageOverallRating,
    };
  }

  /**
   * Method to get product features
   * @param null
   * @return Features[]
   */
  public getProductFeatures(): Features[] {
    return this.features;
  }

  /**
   * Method to get product specifications
   * @param null
   * @return Specification[]
   */
  public getProductSpecifications(): Specification[] {
    return this.specifications;
  }

  /**
   * Method to disable the memory for any unavailable color
   * @return MemoryOption[]
   */
  public setMemoryForColor(): MemoryOption[] {
    let selectedColorMemoryObj;
    let memoryOption = [];
    // Extract Available Memories from ColorOption
    selectedColorMemoryObj = this.colorOptions.find((color: ColorOption) => {
      return color.name === this.selectedColor;
    });
    if (selectedColorMemoryObj && selectedColorMemoryObj.memoryOptions) {
      selectedColorMemoryObj = selectedColorMemoryObj.memoryOptions.map((colorMemory: string) => {
        return colorMemory;
      });
    }
    // Find avaliable memory and set availability of color
    if (this.memoryOptions) {
      memoryOption = this.memoryOptions.map((memory: MemoryOption) => {
        const isMemoryInColor = selectedColorMemoryObj.some((elem: string) => {
          const memorySplit = memory.value.split(' ');
          const elemSplit = elem.split(' ');
          const two = 2;
          if (memorySplit.length <= two) {
            return (
              elemSplit[0] === memorySplit[0] && this.isElemAvailableInItemDetails(elemSplit[0])
            );
          } else {
            // Used to handle occurrence of extra spacing in memory.value
            const formattedMemory = `${memorySplit[0]} ${memorySplit[1]}`;
            return elem === formattedMemory && this.isElemAvailableInItemDetails(elem);
          }
        });
        return {
          name: memory.name,
          value: memory.value,
          available: isMemoryInColor,
        };
      });
    }
    return memoryOption;
  }

  /**
   * Function that returns device details based on device id specific to E-PINS
   */
  public getItemDetailsByDeviceSku(sku: string, objectService: ObjectService): ItemDetail {
    const selectedDevice = this.itemDetails.find((device: ItemDetail) => {
      return objectService.get(device, 'id') === sku;
    });
    return this.getSelectedVarient(selectedDevice || this.itemDetails[0], objectService);
  }

  /**
   * Method to get SelectedVarient
   * @param filteredItem: ItemDetail
   * @param objService: ObjectService
   * @return ItemDetail
   */
  private getSelectedVarient(product: ItemDetail, objService: ObjectService): ItemDetail {
    this.skuId = objService.get(product, 'id') || '';
    const selectedItem = {
      mainImageUrl: product.mainImageUrl || '',
      uSkuIndicator: product.uSkuIndicator,
      memory: product.memory || '',
      color: product.color || '',
      id: product.id,
      salePrice: product.hasSale
        ? this.getSalePrice(product, objService)
        : objService.get(product, 'skuPrice.0.priceSummary.payNowPrice.salePrice.amount') || 0,
      hasSale: product.hasSale,
      hasDeal: product.hasDeal,
      listPrice: this.getListPrice(product, objService) || 0,
      skuImageUrl: product.skuImageUrl,
      itemPromotions: product.hasDeal ? this.getPromotions(product, objService) : null,
      priceWithoutService: this.getPriceWithoutService(product, objService),
      priceWithService: this.getPriceWithService(product, objService),
      serviceThreshold: this.getServiceThreshold(product, objService),
      productPrice: product.hasSale
        ? this.getSalePrice(product, objService)
        : this.getListPrice(product, objService),
      hasCPO: this.hasCPO,
      hasNew: this.hasNew,
      condition: product.condition,
      legalText: this.legalText,
      availabilityStatus: objService.get(product, `availability.availabilityStatus`),
      estimatedShippingFromDateTime: objService.get(
        product,
        `availability.estimatedShippingFromDateTime`
      ),
      estimatedShippingToDateTime: objService.get(
        product,
        `availability.estimatedShippingToDateTime`
      ),
      carousalImages: objService.get(product, `carousalImages`),
      bannerText: objService.get(product, `bannerText`),
      conditionalEnhancement: objService.get(product, `conditionalEnhancement`),
      dealText: objService.get(product, `dealText`),
      deviceSimInfo: objService.get(product, 'deviceSimInfo'),
    };
    return selectedItem;
  }

  private getMemoryMap(): MemoryMap[] {
    const colorArr = [];
    this.colorOptions.forEach((item: ColorOption) => {
      const colorObj = {
        color: item.name,
        memoryArr: this.itemDetails
          .filter((obj: ItemDetail) => {
            return obj.color === item.name;
          })
          .map((data: ItemDetail) => {
            return data.memory;
          }),
      };
      colorArr.push(colorObj);
    });
    return colorArr;
  }

  private isElemAvailableInItemDetails(elem: string): boolean {
    return this.memoryMap
      .find((item: ItemDetail) => {
        return item.color === this.selectedColor;
      })
      .memoryArr.includes(elem);
  }
}
